import {
  Grid,
  Box,
  Chip,
  CircularProgress,
  Typography,
  Select,
  FormControl,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import useInflowComparisonSeries from "../../../hooks/charts/useInflowComparisonSeries";
import MultiYearMonthBarChart from "../yearBarChart/YearBarChart";
import MenuItem from "@material-ui/core/MenuItem";

export default function InflowComparisonPairContainer({
  parameterid,
  years: urlYears,
  allYears,
  flowcond,
  scaletype,
  onYearsChange,
  onScaleChange,
  height = 320,
}) {
  const { cc10, ct2, unit, isLoading, isError } = useInflowComparisonSeries({
    parameterid,
    years: urlYears,
    flowcond,
  });

  const [activeYears, setActiveYears] = useState(urlYears);

  useEffect(() => setActiveYears(urlYears), [urlYears]);

  const toggle = (y) => {
    const next = activeYears.includes(y)
      ? activeYears.filter((n) => n !== y)
      : [...activeYears, y];
    setActiveYears(next);
    onYearsChange(next);
  };

  if (isLoading)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );

  if (isError || !cc10.series.length)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        No data for current selections
      </Box>
    );

  const sharedExtent =
    scaletype === "fixed"
      ? [0, Math.max(cc10.extent[1], ct2.extent[1])]
      : undefined;

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h4" component="h3" align="center">
              CC-10
            </Typography>
            <MultiYearMonthBarChart
              series={cc10.series}
              unit={unit}
              activeYears={[...activeYears].sort()}
              height={height}
              fixedExtent={sharedExtent}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h4" component="h3" align="center">
              CT-2
            </Typography>
            <MultiYearMonthBarChart
              series={ct2.series}
              unit={unit}
              activeYears={[...activeYears].sort()}
              height={height}
              fixedExtent={sharedExtent}
            />
          </Box>
        </Grid>
      </Grid>

      <Box
        component="fieldset"
        mt={2}
        p={2}
        borderColor="grey.300"
        borderRadius={4}
        bgcolor="#f9f9f9"
      >
        <Typography component="legend" variant="subtitle2" gutterBottom>
          Additional Graph Filters
        </Typography>

        {/* Year Selector */}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          flexDirection="column"
          style={{ gap: 16 }}
        >
          <Box display="flex" flexDirection="column" minWidth={200}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Y-Axis Scale Type
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={scaletype}
                onChange={(e) => onScaleChange(e.target.value)}
              >
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="dynamic">Dynamic</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Select Years to Compare
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 2 }}>
              {allYears.map((y) => (
                <Chip
                  key={y}
                  label={y}
                  clickable
                  color={activeYears.includes(y) ? "primary" : "default"}
                  onClick={() => toggle(y)}
                  style={{ width: 60 }}
                  role="checkbox"
                  aria-checked={activeYears.includes(y)}
                  aria-label={`Toggle year ${y}`}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
